import { numFont } from '../../utils/fonts';
import * as React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../utils/languages';
import { formatHours } from './hooks/monthUtils';
import { parseDateKey } from './hooks/dateUtils';

// Typing pauses this long before the grid is asked again with the new threshold.
const OPEN_HOURS_COMMIT_DELAY_MS = 400;
// The server's own rule (OPEN_DAY_FREE_HOURS); the payload echoes what it applied.
const DEFAULT_OPEN_HOURS = 8;

function Tile({ icon: Icon, label, value, sub, color, warn, onClick, compact, testId, tooltip }) {
  // The tooltip covers the label and the number, not the sub-line: the open-days tile keeps
  // an input there with a tooltip of its own.
  const head = (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: compact ? 0.25 : 0.75 }}>
        <Icon sx={{ fontSize: 15, color }} />
        <Typography
          component="span"
          sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        component="div"
        sx={{ fontSize: compact ? '18px' : '22px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.1, color: warn ? '#c05621' : 'text.primary', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {value}
      </Typography>
    </Box>
  );
  return (
    <Box
      data-testid={testId}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      sx={{
        flex: '1 1 150px',
        minWidth: 0,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: warn ? '#fbd9a5' : 'divider',
        borderRadius: '10px',
        p: compact ? '7px 11px' : '11px 13px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 120ms',
        '&:hover': onClick ? { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } : undefined,
      }}
    >
      {tooltip ? <Tooltip title={tooltip}>{head}</Tooltip> : head}
      {sub && (
        <Typography component="div" sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The "does an N h job fit" number inside the open-days rule: "≥ [8] h free". Typing
 * re-asks the server after a short pause; Enter and leaving the field ask at once. An empty
 * or nonsense value falls back to the default rule.
 */
function OpenHoursField({ value, defaultValue, language, onChange }) {
  const { t } = useTranslation();
  const inputRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const effective = value ?? defaultValue;
  const [text, setText] = React.useState(() => formatHours(effective, language));

  // Follow the store when the field is not being typed in (reload, another org, reset).
  React.useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setText(formatHours(effective, language));
    }
  }, [effective, language]);

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  const commit = React.useCallback(
    (raw) => {
      clearTimeout(timerRef.current);
      const parsed = parseFloat(String(raw).trim().replace(',', '.'));
      const next = Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed, 10000) : null;
      if (next !== (value ?? null)) onChange(next);
      return next;
    },
    [value, onChange]
  );

  const handleChange = (event) => {
    const raw = event.target.value;
    if (raw.length > 6) return;
    setText(raw);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(raw), OPEN_HOURS_COMMIT_DELAY_MS);
  };

  const handleBlur = () => {
    const next = commit(text);
    setText(formatHours(next ?? defaultValue, language));
  };

  return (
    <Tooltip title={t('calendar.month.summary.openHoursHelp')} placement="bottom-start" enterDelay={400}>
      <Box
        component="input"
        ref={inputRef}
        data-testid="month-open-hours-input"
        type="text"
        inputMode="decimal"
        aria-label={t('calendar.month.summary.openHoursHelp')}
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        sx={{
          width: '34px',
          mx: '3px',
          px: '3px',
          py: 0,
          fontSize: '11px',
          fontWeight: 700,
          ...numFont,
          textAlign: 'center',
          color: '#0d9488',
          bgcolor: '#f0fdfa',
          border: '1px solid #99f6e4',
          borderRadius: '5px',
          outline: 'none',
          cursor: 'text',
          lineHeight: '16px',
          '&:hover': { borderColor: '#0d9488' },
          '&:focus': { borderColor: '#0d9488', boxShadow: '0 0 0 2px rgba(13,148,136,0.18)' },
        }}
      />
    </Tooltip>
  );
}

/**
 * The numbers a foreman wants before looking at any single day. The unscheduled work is
 * not one of them: it lives in the same sidebar as in the week and day views.
 */
function MonthSummaryStrip({ monthData, compact, openHours, onOpenHoursChange, onJumpToNextFreeDay }) {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n?.language);
  const summary = monthData.summary || {};
  const nextFree = monthData.next_free_day ? parseDateKey(monthData.next_free_day) : null;
  const nextFreeText = nextFree ? `${nextFree.getDate()}.${nextFree.getMonth() + 1}.` : '–';
  // The server echoes the threshold it applied, so with no override the field shows its rule.
  const defaultOpenHours = openHours == null && monthData.open_hours ? monthData.open_hours : DEFAULT_OPEN_HOURS;

  return (
    <Box data-testid="calendar-month-summary" sx={{ display: 'flex', gap: compact ? 1 : 1.25, flexWrap: 'wrap', mb: compact ? 1 : 1.75 }}>
      <Tile
        icon={SpeedIcon}
        label={t('calendar.month.summary.utilization')}
        value={summary.percent !== null && summary.percent !== undefined ? `${summary.percent} %` : '–'}
        sub={`${formatHours(summary.booked_hours, language)} / ${formatHours(summary.available_hours, language)} h`}
        color="#3182ce"
        compact={compact}
        testId="month-summary-utilization"
      />
      <Tile
        icon={BuildIcon}
        label={t('calendar.month.summary.workOrders')}
        value={summary.work_orders ?? 0}
        sub={t('calendar.month.summary.thisMonth')}
        color="#1a365d"
        compact={compact}
        testId="month-summary-work-orders"
      />
      <Tile
        icon={EventAvailableIcon}
        label={t('calendar.month.summary.openDays')}
        value={summary.open_days ?? 0}
        sub={
          onOpenHoursChange ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              ≥
              <OpenHoursField value={openHours} defaultValue={defaultOpenHours} language={language} onChange={onOpenHoursChange} />
              {t('calendar.month.summary.hoursFree')}
            </Box>
          ) : (
            t('calendar.month.summary.openDaysRule')
          )
        }
        color="#0d9488"
        compact={compact}
        onClick={nextFree ? onJumpToNextFreeDay : undefined}
        tooltip={nextFree ? t('calendar.month.summary.jumpToNextFree', { date: nextFreeText }) : t('calendar.month.summary.noFreeDay')}
        testId="month-summary-open-days"
      />
      {monthData.parts_tracking_enabled && (
        <Tile
          icon={ErrorOutlineIcon}
          label={t('calendar.month.summary.partsMissing')}
          value={summary.parts_missing ?? 0}
          sub={t('calendar.month.summary.onWorkOrders')}
          color="#c05621"
          warn={(summary.parts_missing ?? 0) > 0}
          compact={compact}
          testId="month-summary-parts"
        />
      )}
      <Tile
        icon={PersonOffIcon}
        label={t('calendar.month.summary.absenceDays')}
        value={summary.absence_days ?? 0}
        sub={t('calendar.month.summary.reducesCapacity')}
        color="#d53f8c"
        compact={compact}
        testId="month-summary-absences"
      />
    </Box>
  );
}

export default MonthSummaryStrip;
